import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import SettingCard from '../../settings/SettingCard';
import * as styles from './TopLevelTopicSettings.module.scss';

const SETTING_FRAGMENT = gql(`
  fragment TopLevelTopicSettings_settings on OrganizationSettings {
    allowUserRootTopic
  }
`);

const UPDATE_TOP_LEVEL_TOPIC_SETTINGS_MUTATION = gql(/** @complexity 4 */ `
  mutation updateTopLevelTopicSettings($allowUserRootTopic: Boolean!) {
    organization: updateOrganization(settings: { allowUserRootTopic: $allowUserRootTopic }) {
      id
      settings {
        ...TopLevelTopicSettings_settings
      }
    }
  }
`);

const OPTIONS = {
  enabled: 'Users can make topics top level',
  disabled: 'Only admins can make topics top level',
} as const;

type Values = {
  allowUserRootTopic: keyof typeof OPTIONS;
};

interface TopLevelTopicSettingsProps {
  settings: DocumentType<typeof SETTING_FRAGMENT>;
}

const TopLevelTopicSettings = ({ settings }: TopLevelTopicSettingsProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const [updateTopLevelTopicSettings] = useMutation(
    UPDATE_TOP_LEVEL_TOPIC_SETTINGS_MUTATION,
  );

  const allowUserRootTopic = settings.allowUserRootTopic
    ? 'enabled'
    : 'disabled';

  const handleSubmit = async (values: Values) => {
    await updateTopLevelTopicSettings({
      variables: {
        allowUserRootTopic: values.allowUserRootTopic === 'enabled',
      },
    });
    setIsEditing(false);
  };

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Top level topics"
          description={
            <Form<Values>
              initialValues={{ allowUserRootTopic }}
              onSubmit={handleSubmit}
            >
              <div className={styles.formFields}>
                {(['enabled', 'disabled'] as const).map((value) => (
                  <Field
                    key={value}
                    label={OPTIONS[value]}
                    name="allowUserRootTopic"
                    type="radio"
                    value={value}
                  />
                ))}
              </div>
              <div className={styles.description}>
                Regardless of selection, guests cannot create top level topics.
              </div>
              <div className={styles.formFooter}>
                <Submit variant="primary">Save</Submit>
              </div>
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            <Button onClick={() => setIsEditing(true)} variant="secondary">
              Edit
            </Button>
          }
          title="Top level topics"
          description={OPTIONS[allowUserRootTopic]}
        />
      )}
    </>
  );
};

export default TopLevelTopicSettings;
